import type { ReactNode } from 'react';

import React from 'react';
import { Link } from 'react-router';
import { createPortal } from 'react-dom';

import { cn } from '@shared/utils/cn';
import { usePageHeaderTarget } from '@shared/components/page-header';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@shared/components/ui/breadcrumb';

export type WarehouseBreadcrumbLink = {
  name?: string;
  href?: string;
  icon?: ReactNode;
};

/**
 * Page heading + breadcrumb trail ("Dashboard" first, the last item is the current page).
 * Rendered in the layout's header slot when there is one (saves vertical space), inline otherwise.
 * In the header the trail is hidden below `sm`: phones only keep the title.
 */
export const WarehouseBreadcrumbs = ({
  page,
  breadcrumbs,
}: {
  page?: string;
  shop?: string;
  breadcrumbs?: WarehouseBreadcrumbLink[];
}) => {
  const heading = page ? `Entrepôt - ${page}` : 'Entrepôt';
  const headerTarget = usePageHeaderTarget();
  const links: WarehouseBreadcrumbLink[] = [{ name: 'Dashboard', href: '/' }, ...(breadcrumbs ?? [])];

  const trail = (
    <Breadcrumb className={cn(headerTarget && 'hidden sm:block')}>
      <BreadcrumbList className={cn(headerTarget && 'flex-nowrap gap-x-2 text-xs whitespace-nowrap')}>
        {links.map(({ name, href, icon }, index) => {
          const content = (
            <>
              {icon}
              {name}
            </>
          );
          const isLast = index === links.length - 1;

          return (
            <React.Fragment key={name ?? index}>
              {index > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem className="gap-1">
                {isLast && <BreadcrumbPage className="inline-flex items-center gap-1">{content}</BreadcrumbPage>}
                {!isLast && href && (
                  <BreadcrumbLink asChild className="inline-flex items-center gap-1">
                    <Link to={href}>{content}</Link>
                  </BreadcrumbLink>
                )}
                {!isLast && !href && <span className="inline-flex items-center gap-1">{content}</span>}
              </BreadcrumbItem>
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );

  if (headerTarget) {
    return createPortal(
      <div className="flex min-w-0 flex-col justify-center" data-slot="page-title">
        <h1 className="truncate text-base leading-tight font-bold lg:text-lg">{heading}</h1>
        {trail}
      </div>,
      headerTarget
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <h1 className="text-xl leading-9 font-bold md:text-2xl">{heading}</h1>
      {trail}
    </div>
  );
};
